import dynamic from "next/dynamic"
import { Layout } from "components/common"
import BoxRelatedTo from "components/BoxRelatedTo"
import NextHead from "next/head"
import config from "lib/config"
import fetchWithTimeout from "api/fetchWithTimeout"
import ViewPlay from "components/ViewPlay"
import { useEffect, useRef, useState } from "react"
import { convertTypeVideo, convertTypeVideoDetail, trackGA } from "lib/func"
import axios from "axios"
import { getAccessToken } from "lib/Cookies"
import { notification } from "components/ui"
import Link from "next/link"

const Share = dynamic(() => import("components/Share"), { ssr: false })
export default function Video({ initVideo, query }) {
  const {
    name,
    thumbnail,
    created,
    league_name,
    category_id,
    category_name,
    type,
    league_id,
    is_live,
    age,
    descriptions
  } = initVideo

  useEffect(() => {
    trackGA(initVideo?.name, convertTypeVideo(type))
    trackGA(initVideo?.name, convertTypeVideoDetail(type))
  }, [])

  const [isSaved, setIsSaved] = useState(false)
  const [isSavedVideoRcm, setIsSavedVideoRcm] = useState(false)
  const [watchLaterList, setWatchLaterList] = useState([])
  const [isExpanded, setIsExpanded] = useState(false)
  const [showToggle, setShowToggle] = useState(false)
  const descriptionRef = useRef(null)
  const [isMobile, setIsMobile] = useState(false)
  const [isShow, setIsShow] = useState(false)
  const [showPopup, setShowPopup] = useState(false)
  const [shareVideo, setShareVideo] = useState(initVideo)
  const divRef = useRef(null)

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 768) {
        setIsMobile(true)
      } else {
        setIsMobile(false)
      }
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    function checkHeight() {
      if (descriptionRef.current) {
        const lineHeight = parseFloat(getComputedStyle(descriptionRef.current).lineHeight)
        const maxLines = 4
        const maxHeight = lineHeight * maxLines
        setShowToggle(descriptionRef.current.scrollHeight > maxHeight)
      }
    }

    checkHeight()
    window.addEventListener("resize", checkHeight)
    return () => {
      window.removeEventListener("resize", checkHeight)
    }
  }, [descriptions, isExpanded])

  const toggleDescription = () => {
    setIsExpanded((prev) => !prev)
  }

  const fetchWatchLaterList = async () => {
    try {
      const response = await axios.get(`${process.env.NEXT_PUBLIC_TV}/api/v2/publish/user/watch-later`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`
        }
      })

      if (response.status === 200) {
        const list = response.data.data
        setWatchLaterList(list)
        const isVideoSaved = list.some((item) => item.id === initVideo.id)
        setIsSaved(isVideoSaved)
      }
    } catch (error) {
      return []
    }
  }

  const toggleWatchLater = async () => {
    const token = getAccessToken()
    if (!token) {
      notification.warning({
        message: "Vui lòng đăng nhập để sử dụng tính năng"
      })
      return
    }
    try {
      if (isSaved) {
        notification.warning({
          message: "Video đã được thêm vào danh sách trước đó"
        })
      } else {
        const response = await axios.post(
          `${process.env.NEXT_PUBLIC_DOMAIN_AUTH}/api/video/watch_later`,
          {
            video_id: initVideo.id
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          }
        )

        if (response.status === 200) {
          notification.success({
            message: "Đã lưu nội dung vào dánh sách xem sau"
          })
          setIsSaved(true)
        }
      }
    } catch (error) {
      console.error("Error in toggleWatchLater:", error) // Log the error for debugging
      notification.error({ message: "Có lỗi xảy ra vui lòng thử lại sau!" }) // Notify the user
    }
  }

  const toggleWatchLaterVideoRcm = async () => {
    const token = getAccessToken()
    if (!token) {
      notification.warning({
        message: "Vui lòng đăng nhập để sử dụng tính năng"
      })
      return
    }
    try {
      if (isSavedVideoRcm) {
        notification.warning({
          message: "Video đã được thêm vào danh sách trước đó"
        })
      } else {
        const response = await axios.post(
          `${process.env.NEXT_PUBLIC_DOMAIN_AUTH}/api/video/watch_later`,
          {
            video_id: shareVideo.id
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          }
        )

        if (response.status === 200) {
          notification.success({
            message: "Đã lưu nội dung vào dánh sách xem sau"
          })
          setIsSavedVideoRcm(true)
        }
      }
    } catch (error) {
      console.error("Error in toggleWatchLater:", error) // Log the error for debugging
      notification.error({ message: "Có lỗi xảy ra vui lòng thử lại sau!" }) // Notify the user
    }
  }

  useEffect(() => {
    fetchWatchLaterList()
  }, [initVideo.id])

  useEffect(() => {
    function handleClickOutside(event) {
      if (divRef.current && !divRef.current.contains(event.target)) {
        setIsShow(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isShow])

  const handleClick = () => {
    setShowPopup(true)
    setIsShow(false)
  }

  return (
    <section className="mx-auto overflow-x-hidden">
      <NextHead>
        <title>{name}</title>
        <meta name="description" content={name} />
        {/* Facebook */}
        <meta property="og:title" key="title" content={name} itemProp="headline" />
        <meta property="og:description" key="description" content={name} itemProp="description" />
        <meta property="og:image" key="image" itemProp="thumbnailUrl" content={thumbnail} />
        <meta
          property="og:url"
          key="url"
          itemProp="url"
          content={`https://onlivetv.vn/video?id=${query.id}&type=${query.type}`}
        />
        <meta key="imageWidth" property="og:image:width" content="640" />
        <meta key="imageHeight" property="og:image:height" content="360" />
        {/* Twitter  */}
        <meta name="twitter:card" value="summary" />
        <meta name="twitter:title" key="tw-title" content={name} />
        <meta name="twitter:description" key="tw-description" content={name} />
        <meta name="twitter:image" key="tw-image" content={thumbnail} />
        <meta name="twitter:image:width" key="tw-width" content="640" />
        <meta name="twitter:image:height" key="tw-height" content="360" />
        <meta
          name="twitter:url"
          itemProp="url"
          key="tw-url"
          content={`https://onlivetv.vn/video?id=${query.id}&type=${query.type}`}
        />
        <meta name="twitter:site" content="@onlivetv" />
        <meta name="twitter:creator" content="@onlivetv" />
      </NextHead>

      <div className="pb-8">
        <div className="flex max-lg:flex-col gap-6 max-md:gap-3 max-lg:gap-5 max-sm:pt-[55%]">
          <div className="flex flex-col gap-4 max-md:gap-3 max-lg:px-0 lg:pl-8 min-[1200px]:pl-0 relative w-[1177px] max-lg:w-full max-lg:max-w-[100vw]">
            <div className="max-sm:fixed top-[60px] z-10 max-sm:w-full">
              <ViewPlay initVideo={initVideo} />
            </div>
            <div className="flex flex-col gap-3 max-lg:px-4">
              <div className="flex flex-col gap-3 max-sm:justify-between max-sm:flex-row max-sm:mt-2">
                <div className="text-base leading-[22px] md:text-xl text-[#fafaf9] font-semibold max-sm:line-clamp-2">
                  {name}
                </div>
                <div
                  className="w-[20px] h-[20px] hidden max-sm:block cursor-pointer"
                  onClick={() => {
                    setShareVideo(initVideo)
                    setIsShow(!isShow)
                    setShowPopup(false)
                  }}
                >
                  <img src="/icons/option.png" alt="more" />
                </div>
              </div>
              <div className="flex justify-between gap-3 items-center font-bold leading-5">
                <div className="flex gap-2">
                  {age && !isMobile && (
                    <div className="py-2.5 px-3 bg-[#2E2E2E] rounded-[32px]">
                      <span className="font-medium text-sm text-[#fafaf9] line-clamp-1">{age}</span>
                    </div>
                  )}
                  {category_name && (
                    <Link href={`/category?id=${category_id}`}>
                      <div className="py-2.5 px-3 bg-[#0C3B80] rounded-[32px] hover:bg-primary">
                        <span className="font-medium text-sm text-[#fafaf9] line-clamp-1">{category_name}</span>
                      </div>
                    </Link>
                  )}
                  {created && (
                    <Link href={`/league?id=${league_id}`}>
                      <div className="py-2.5 px-3 bg-[#0C3B80] rounded-[32px] hover:bg-primary">
                        <span className="font-medium text-sm text-[#fafaf9] line-clamp-1">{league_name}</span>
                      </div>
                    </Link>
                  )}
                </div>
                <div className="flex gap-3">
                  <Share
                    initVideo={isMobile ? shareVideo : initVideo}
                    showPopup={showPopup}
                    handleCancel={() => setShowPopup(false)}
                  />
                  {!is_live && (
                    <button onClick={toggleWatchLater}>
                      {isMobile ? (
                        isSaved ? (
                          <div className="bg-primary rounded-xl py-2 px-4">
                            <img src="/icons/iconWatchLaterSaved.svg" alt="" />
                          </div>
                        ) : (
                          <div className="bg-primary rounded-xl py-2 px-4 hover:bg-[#104FA3]">
                            <img src="/icons/iconWatchLater.svg" alt="" />
                          </div>
                        )
                      ) : isSaved ? (
                        <div className="flex gap-2 text-sm text-[#fafaf9] font-medium bg-primary rounded-xl py-2.5 px-4 whitespace-nowrap">
                          <img src="/icons/iconWatchLaterSaved.svg" alt="" /> Xem sau
                        </div>
                      ) : (
                        <div className="flex gap-2 text-sm text-[#fafaf9] font-medium bg-primary rounded-xl py-2.5 px-4 hover:bg-[#104FA3] whitespace-nowrap">
                          <img src="/icons/iconWatchLater.svg" alt="" /> Xem sau
                        </div>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="font-normal text-base max-md:text-sm leading-5 text-[#BCBCBC] max-lg:px-4">
              <div ref={descriptionRef} className={`${!isExpanded ? "line-clamp-4" : ""}`}>
                {descriptions}
              </div>
              {showToggle && (
                <button className="font-bold text-xs hover:underline text-white" onClick={toggleDescription}>
                  {isExpanded ? "Ẩn bớt" : "thêm"}
                </button>
              )}
            </div>
          </div>
          <div className="flex max-lg:max-h-full max-h-[900px] max-lg:px-4 lg:pr-8 w-[376px] max-lg:w-full max-lg:max-w-[100vw] grow">
            <BoxRelatedTo
              query={query}
              shareVideo={shareVideo}
              setShareVideo={setShareVideo}
              setShowPopup={setShowPopup}
              isShow={isShow}
              setIsShow={setIsShow}
              watchLaterList={watchLaterList}
              setIsSavedVideoRcm={setIsSavedVideoRcm}
            />
          </div>
        </div>
      </div>

      {isMobile && isShow && (
        <nav
          className="fixed bottom-0 w-full bg-[#2F3037] shadow-md justify-around py-6 px-4 animate-slideUp rounded-t-2xl"
          ref={divRef}
        >
          <div className="absolute top-2 left-1/2 -translate-x-1/2 border-t-4 border-[#63656e] w-[64px] rounded"></div>
          {shareVideo?.is_live === false ? (
            <div className="flex flex-col gap-6 py-3 mb-[14px]">
              {shareVideo?.id !== initVideo?.id &&
                (isSavedVideoRcm ? (
                  <div
                    className="flex items-center gap-2 text-sm text-[#F4F4F8] font-medium whitespace-nowrap py-2"
                    onClick={toggleWatchLaterVideoRcm}
                  >
                    <img src="/icons/iconWatchLaterSaved.svg" width={24} /> Đã thêm vào danh sách xem sau
                  </div>
                ) : (
                  <div
                    className="flex items-center gap-2 text-sm text-[#F4F4F8] font-medium whitespace-nowrap py-2"
                    onClick={toggleWatchLaterVideoRcm}
                  >
                    <img src="/icons/iconWatchLater.svg" width={24} /> Xem sau
                  </div>
                ))}
              <button
                className="flex items-center w-full gap-2 text-sm font-medium text-[#F4F4F8] py-2"
                onClick={handleClick}
              >
                <img src="/icons/iconShare.svg" width={24} />
                Chia sẻ
              </button>
            </div>
          ) : (
            <button
              className="flex items-center w-full gap-2 text-sm font-medium text-[#F4F4F8] py-2 mt-3 mb-[26px]"
              onClick={handleClick}
            >
              <img src="/icons/iconShare.svg" width={24} />
              Chia sẻ
            </button>
          )}
        </nav>
      )}
    </section>
  )
}

Video.Layout = Layout

export async function getServerSideProps({ query }) {
  let initVideo = {},
    error = {}
  try {
    const params = { ...query, platform: "widevine" }
    const response = await fetchWithTimeout(`${config.BASE_API_URL}/publish/event/?` + new URLSearchParams(params))
    const res = await response?.json()
    if (response?.ok) {
      if (res?.data) initVideo = res?.data
    } else {
      error = res
    }
  } catch (error) {
    console.log(error)
  }
  if (error?.error_code) initVideo.error = error
  return { props: { initVideo, query } }
}
